<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { RouterLink, useRoute } from 'vue-router';
import { isWorkspaceV2Path } from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { evaluateSLAStatus } from '@chatwoot/utils';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
import { useMessagingWindow } from 'dashboard/composables/useMessagingWindow';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

// SiteUp: faixa lateral de urgência no card, combinando prioridade + SLA.
// Vermelha = SLA estourado OU prioridade urgente; Âmbar = SLA perto de estourar
// OU prioridade alta. Sem faixa nos demais casos (lista limpa).
const snakeCase = object =>
  Object.keys(object || {}).reduce((acc, key) => {
    acc[key.replace(/([A-Z])/g, '_$1').toLowerCase()] = object[key];
    return acc;
  }, {});

const slaUrgency = computed(() => {
  const appliedSla = props.conversation?.appliedSla;
  if (!appliedSla?.id) return null;
  const status = evaluateSLAStatus({
    appliedSla: snakeCase(appliedSla),
    chat: props.conversation,
  });
  if (status?.isSlaMissed) return 'high';
  if (status?.threshold) return 'medium';
  return null;
});

const urgencyLevel = computed(() => {
  const priority = props.conversation?.priority;
  const sla = slaUrgency.value;
  if (priority === 'urgent' || sla === 'high') return 'high';
  if (priority === 'high' || sla === 'medium') return 'medium';
  return null;
});

const urgencyStripeClass = computed(
  () =>
    ({
      high: 'bg-n-ruby-9',
      medium: 'bg-n-amber-9',
    })[urgencyLevel.value] || ''
);

// SiteUp: CSW timer + optional FEP badge on list cards.
const {
  hasWindow: hasMessagingWindow,
  shortLabel: messagingWindowShort,
  badgeClass: messagingWindowBadgeClass,
  icon: messagingWindowIcon,
  tooltip: messagingWindowTooltip,
  isUrgent: messagingWindowUrgent,
  fepActive: messagingWindowFepActive,
  fepShortLabel: messagingWindowFepShort,
} = useMessagingWindow(() => props.conversation);

const conversationRoute = computed(() => {
  const fromWorld =
    typeof route.query.from_world === 'string'
      ? worldById(route.query.from_world)?.id
      : null;
  return {
    path: `/app/accounts/${route.params.accountId}/${isWorkspaceV2Path(route.path) ? 'v2/' : ''}conversations/${props.conversation.id}`,
    query: fromWorld ? { from_world: fromWorld } : {},
  };
});
</script>

<template>
  <RouterLink
    :to="conversationRoute"
    class="relative flex w-full gap-3 px-3 py-4 text-inherit no-underline transition-all duration-300 ease-in-out cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
  >
    <!-- SiteUp: faixa lateral de urgência (prioridade + SLA) -->
    <span
      v-if="urgencyStripeClass"
      class="absolute inset-y-0 ltr:left-0 rtl:right-0 w-1 rounded-full"
      :class="urgencyStripeClass"
      aria-hidden="true"
    />
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <span
            v-if="hasMessagingWindow"
            v-tooltip.left="messagingWindowTooltip"
            class="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full text-[11px] font-medium flex-shrink-0"
            :class="[
              messagingWindowBadgeClass,
              messagingWindowUrgent && 'animate-pulse',
            ]"
          >
            <span :class="messagingWindowIcon" class="size-3" />
            {{ messagingWindowShort }}
          </span>
          <span
            v-if="messagingWindowFepActive"
            class="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full text-[11px] font-medium flex-shrink-0 bg-n-teal-9/15 text-n-teal-11"
          >
            <span class="i-ri-discount-percent-line size-3" />
            {{ messagingWindowFepShort }}
          </span>
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </RouterLink>
</template>
